import React, { useMemo } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { DashboardDataRaw, DateRange, Granularity } from '../../types/Dashboard';
import { Panel } from '../ui';
import {
  formatCompact,
  formatCurrency,
  formatNumber,
  legendStyle,
  tooltipStyle,
  tickStyle,
  axisTitleStyle,
  yGrid,
} from './dashboardUi';
import {
  bucketize,
  activesAtDate,
  addDays,
  exclusive8080AutoRevenue,
  manualOverrideForMonth,
} from './dashboardHelpers';

ChartJS.register(...registerables);

interface Props {
  data: DashboardDataRaw;
  range: DateRange;
  granularity: Granularity;
}

const EXCLUSIVE_PLAN = 'Exclusive 8080';

const GRAN_LABEL: Record<Granularity, string> = {
  day: 'día',
  week: 'semana',
  month: 'mes',
};

/** Degradado bajo la curva de activos: da volumen sin tapar la grilla. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function areaFill(ctx: any) {
  const { chart } = ctx;
  const { ctx: canvas, chartArea } = chart;
  if (!chartArea) return 'rgba(37,99,235,0.12)';
  const gradient = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, 'rgba(37,99,235,0.22)');
  gradient.addColorStop(1, 'rgba(37,99,235,0.01)');
  return gradient;
}

const EvolutionChart: React.FC<Props> = ({ data, range, granularity }) => {
  const { labels, acumulados, revenues } = useMemo(() => {
    const buckets = bucketize(range, granularity);
    const exclusiveHistory = data.manualRevenueHistory[EXCLUSIVE_PLAN] ?? [];

    const labels: string[] = [];
    const acumulados: number[] = [];
    const revenues: number[] = [];

    for (const bucket of buckets) {
      // end es exclusivo, así que el snapshot es al cierre del último día del bucket
      const snapshotDate = addDays(bucket.end, -1);

      const activesSnap = activesAtDate(data.premium, snapshotDate);
      const totalActivos = Object.values(activesSnap).reduce((s, n) => s + n, 0);

      let rev = 0;
      for (const [plan, count] of Object.entries(activesSnap)) {
        if (plan === EXCLUSIVE_PLAN) continue;
        rev += count * (data.precios[plan] ?? 0);
      }
      const exclusiveOverride = manualOverrideForMonth(exclusiveHistory, snapshotDate);
      if (exclusiveOverride) {
        rev += exclusiveOverride.monthly_amount;
      } else {
        rev += exclusive8080AutoRevenue(data.premium, snapshotDate, EXCLUSIVE_PLAN).revenue;
      }

      labels.push(bucket.label);
      acumulados.push(totalActivos);
      revenues.push(rev);
    }

    return { labels, acumulados, revenues };
  }, [data, range, granularity]);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Activos acumulados',
        data: acumulados,
        borderColor: '#2563eb',
        backgroundColor: areaFill,
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: '#2563eb',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        yAxisID: 'y',
        tension: 0.3,
        fill: true,
      },
      {
        label: 'Revenue al cierre (ARS)',
        data: revenues,
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        yAxisID: 'y1',
        tension: 0.3,
        borderDash: [6, 4],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: legendStyle,
      tooltip: {
        ...tooltipStyle,
        callbacks: {
          label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
            const label = ctx.dataset.label ?? '';
            const val = ctx.parsed.y ?? 0;
            if (label.includes('Revenue')) return ` ${label}: ${formatCurrency(val)}`;
            return ` ${label}: ${formatNumber(val)}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { ...tickStyle, maxRotation: 0, autoSkipPadding: 16 },
        grid: { display: false },
        border: { color: '#e2e8f0' },
      },
      y: {
        type: 'linear' as const,
        position: 'left' as const,
        title: { display: true, text: 'Activos premium', ...axisTitleStyle },
        beginAtZero: true,
        ticks: { ...tickStyle, callback: (v: string | number) => formatNumber(Number(v)) },
        grid: yGrid,
        border: { display: false },
      },
      y1: {
        type: 'linear' as const,
        position: 'right' as const,
        title: { display: true, text: 'Revenue recurrente (ARS)', ...axisTitleStyle },
        beginAtZero: true,
        ticks: { ...tickStyle, callback: (v: string | number) => formatCompact(Number(v)) },
        grid: { drawOnChartArea: false },
        border: { display: false },
      },
    },
  };

  return (
    <Panel
      title={`Evolución por ${GRAN_LABEL[granularity]}`}
      subtitle={`Foto al cierre de cada ${GRAN_LABEL[granularity]}: activos y revenue con los datos vigentes a esa fecha. La línea punteada usa el eje de la derecha.`}
    >
      <div className="h-72">
        <Line data={chartData} options={options} />
      </div>
    </Panel>
  );
};

export default EvolutionChart;
